import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { ColorModeContext, tokens } from "../../theme";
import { useNavigate } from "react-router-dom";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import Badge from "@mui/material/Badge";
import { useAuth } from "../../AuthContext";
import LogoBlog from "../../assets/logo.png";
import notificationSound from "../../assets/notification/newnotification.mp3";

import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import BroadcastOnHomeIcon from "@mui/icons-material/BroadcastOnHome";

const TopBar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode) || {};
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const { getData, addData, userID, userRole } = useAuth();

  const [messageCount, setMessageCount] = useState(0);
  const [readBy, setReadBy] = useState([]);
  const [messageNumber, setMessageNumber] = useState(0);
  const [messages, setMessages] = useState([]);
  const [showMessages, setShowMessages] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const audioRef = useRef(null);
  const lastPlayedMessageIds = useRef(new Set());

  useEffect(() => {
    const handleUserInteraction = () => {
      setUserInteracted(true);
      window.removeEventListener("click", handleUserInteraction);
    };

    window.addEventListener("click", handleUserInteraction);
    return () => window.removeEventListener("click", handleUserInteraction);
  }, []);

  useEffect(() => {
  audioRef.current = new Audio(notificationSound);
  audioRef.current.volume = 1;

  const fetchMessages = async () => {
    try {
      const responseReadBy = await getData("proccess.php", "readBy_V2");
      setReadBy(responseReadBy.data);

      const response = await getData("proccess.php", "broadcast_V2");
      const newMessages = response.data || [];
      setMessages(newMessages);

      // Check for new unread messages
      const newUnreadMessages = newMessages.filter(
        (bct) => !responseReadBy.data.find(
          (msg) => msg.broadcastId === bct.id && msg.userId === userID
        )
      );

      // Update message count
      setMessageCount(newUnreadMessages.length);

      // Play sound only if there are new unread messages and not already played
      if (newUnreadMessages.length > 0 && !lastPlayedMessageIds.current.has(newUnreadMessages[0].id)) {
        playNotificationSound();
        newUnreadMessages.forEach((msg) => lastPlayedMessageIds.current.add(msg.id));
      }

      const responseMessage = await getData("proccess.php", "messages_V2");
      const newMessagesResponse = responseMessage.data || [];

      const unreadCountNumber = newMessagesResponse.filter(
        (msg) => msg.status === 0 && msg.receiver === userID
      ).length;
      setMessageNumber(unreadCountNumber);
    } catch (error) {
      console.error("Failed to fetch messages from broadcast_V2 table:", error);
    }
  };

  fetchMessages();
  const intervalId = setInterval(fetchMessages, 10000);

  return () => {
    clearInterval(intervalId);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };
}, [userID, getData, userInteracted]);

  const markAsRead = async (messageId) => {
    try {
      const isRead = readBy.find(user => user.userId === userID && user.broadcastId === messageId);
      if(!isRead){
        const response = await addData(
          "proccess.php",
          "readBy_V2",
          { 
            broadcastId: messageId,
            userId: userID
          }
        );
        if (response.success) {
          //console.log(response.message);
          // Update local state to reflect the read status
          setReadBy(response.data);
          // Update message count
          setMessageCount((prevCount) => prevCount - 1);
        } else {
          console.error("Failed to mark message as read:", response.message);
        }
      }

    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };

  const playNotificationSound = () => {
    try {
      const audio = audioRef.current;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          if (userInteracted) {
            console.warn("Audio playback prevented:", error);
          }
        });
      }
    } catch (error) {
      console.error("Audio playback failed:", error);
    }
  };

  const toggleMessages = () => {
    setShowMessages((prev) => !prev);
  };

  const formatTime = (timeString) => {
    if (timeString === "Just now") return timeString;
    try {
      const time = new Date(timeString);
      if (isNaN(time.getTime())) return "Invalid time";
      return time.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid time";
    }
  };

  const getColor = (path, fallback) => {
    try {
      return path.split(".").reduce((o, i) => o[i], colors) || fallback;
    } catch {
      return fallback;
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      p={2}
      position="relative"
    >
      <Box display="flex" borderRadius="3px" maxWidth={{ xs: '80px', sm: '100px', md: '120px'}}>
        <div className="small-img">
          <img className="image" src={LogoBlog} alt="Logo" />
        </div>
      </Box>

      
      <Box display={{ xs: 'none', sm: 'flex', md: 'flex'}} gap="1rem">
        {userRole === "receptionist" || userRole === 'user' ? null : (
          <Box display="flex" alignItems="center" justifyContent="center">
            <IconButton onClick={() => navigate("/website/dashboard")}>
              <LanguageOutlinedIcon sx={{fontSize: { xs: '.5rem', sm: '1rem', md: '1.5rem'}}}/>
            </IconButton>
            <Typography
              sx={{
                display: {xs: 'none', sm: 'none', md: 'flex'},
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Manage Website
            </Typography>
          </Box>
        )}
        {userRole === "receptionist" || userRole === 'user' ? null : (
          <Box display="flex" alignItems="center" justifyContent="center">
            <IconButton
              onClick={() => navigate("/admin/dashboard/password-reset")}
            >
              <ManageAccountsIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
            </IconButton>
            <Typography
              sx={{
                display: {xs: 'none', sm: 'none', md: 'flex'},
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Staffs Password Reset
            </Typography>
          </Box>
        )}

        
          <Box display="flex" alignItems="center" justifyContent="center">
            <IconButton onClick={() => navigate("/admin/dashboard/broadcast")}>
              <BroadcastOnHomeIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
            </IconButton>
            <Typography
              sx={{
                display: {xs: 'none', sm: 'none', md: 'flex'},
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Broadcast Message
            </Typography>
          </Box>
      </Box>

      <Box display="flex" alignItems="center" gap="10px">
        <IconButton onClick={colorMode.toggleColoeMode}>
          {theme.palette.mode === "dark" ? (
            <DarkModeOutlinedIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
          ) : (
            <LightModeOutlinedIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
          )}
        </IconButton>

        <Box position="relative">
          <IconButton onClick={toggleMessages}>
            <Badge
              color="error"
              badgeContent={messageCount}
              max={99}
              invisible={messageCount === 0}
              sx={{
                "& .MuiBadge-badge": {
                  animation: messageCount > 0 ? "pulse 1.5s infinite" : "none",
                  right: 4,
                  top: 4,
                },
              }}
            >
              <NotificationsOutlinedIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
            </Badge>
          </IconButton>

          {showMessages && (
            <Box
              position="absolute"
              right={0}
              width={{ xs: '200px', sm: '320px', md: '320px'}}
              bgcolor={getColor("primary.500", "#1a1a1a")}
              boxShadow={3}
              borderRadius="4px"
              zIndex="tooltip"
              maxHeight="400px"
              overflow="auto"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={2}
                borderBottom={`1px solid ${getColor("grey.700", "#e0e0e0")}`}
              >
                <Typography variant="h6">Messages</Typography>
              </Box>

              {messages.length === 0 ? (
                <Box p={2} textAlign="center">
                  <Typography variant="body2">No messages</Typography>
                </Box>
              ) : (
                messages.map(
                  (message) =>
                    message.status === 0 ? (
                      <Box
                        key={message.id}
                        p={2}
                        sx={{
                          borderBottom: `1px solid ${getColor(
                            "grey.700",
                            "#e0e0e0"
                          )}`,
                          backgroundColor:
                            message.status === 0
                              ? getColor("primary.600", "#2a2a2a")
                              : getColor("primary.500", "#1a1a1a"),
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: getColor(
                              "blueAccent.800",
                              "#4d4d4e"
                            ),
                          },
                        }}
                        onClick={() => markAsRead(message.id)}
                      >
                        <Typography variant="body1">
                          {message.message}
                          {message.status === 0 && " 🔔"}
                          {message.senderID === userID && " ↗️"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatTime(message.date_created)}
                        </Typography>
                      </Box>
                    ):(
                      <Box
                        key={message.id}
                        p={2}
                        sx={{
                          borderBottom: `1px solid ${getColor(
                            "grey.700",
                            "#e0e0e0"
                          )}`,
                          backgroundColor:
                            message.status === 0
                              ? getColor("primary.600", "#2a2a2a")
                              : getColor("primary.500", "#1a1a1a"),
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: getColor(
                              "blueAccent.800",
                              "#4d4d4e"
                            ),
                          },
                        }}
                        onClick={() => markAsRead(message.id)}
                      >
                        <Typography variant="body1">
                          {message.message}
                          {message.status === 0 && " 🔔"}
                          {message.senderID === userID && " ↗️"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatTime(message.date_created)}
                        </Typography>
                      </Box>
                    )
                )
              )}
            </Box>
          )}
        </Box>
        {userRole === 'user' ? (
          <IconButton onClick={() => navigate("/user/dashboard")}>
            <Badge
              color="error"
              badgeContent={messageNumber}
              max={99}
              invisible={messageNumber === 0}
              sx={{
                "& .MuiBadge-badge": {
                  animation: messageNumber > 0 ? "pulse 1.5s infinite" : "none",
                  right: 4,
                  top: 4,
                },
              }}
            >
              <PersonOutlineOutlinedIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
            </Badge>
          </IconButton>
        ) : (
          <IconButton onClick={() => navigate("/admin/dashboard/profile")}>
            <Badge
              color="error"
              badgeContent={messageNumber}
              max={99}
              invisible={messageNumber === 0}
              sx={{
                "& .MuiBadge-badge": {
                  animation: messageNumber > 0 ? "pulse 1.5s infinite" : "none",
                  right: 4,
                  top: 4,
                },
              }}
            >
              <PersonOutlineOutlinedIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
            </Badge>
          </IconButton>
        )}

        {userRole === 'user' ? null : (
          <IconButton onClick={() => navigate("/admin/dashboard/signout")}>
            <LogoutOutlinedIcon sx={{fontSize: { xs: '.8rem', sm: '1rem', md: '1.5rem'}}}/>
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default TopBar;
